import React, { Suspense, useEffect, useMemo, useState } from "react";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import MethodsContainer from "components/payments/methods/MethodsContainer";
import GiftCard from "components/payments/methods/GiftCard";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import DividerWithLabel from "components/common/DividerWithLabel";
import { useLocale } from "lib/hooks/useLocale";
import { UPICollect } from "./upi/CollectAndIntent";
import CustomPayment from "./CustomPayment";
import { PaymentModeUiConfigBE } from "lib/types/merchantConfigTypes";
import ShopifyGiftCard from "./ShopifyGiftCard";
import { PaymentMode } from "lib/types/payments";
import { SELF_HANDLED_PAYMENT_MODES } from "lib/utils/constants";
import { isS2SVerifiedForUpiIntentTile } from "lib/utils/helpers";
import { getUpiIntentModeConfig } from "lib/utils/payments";
import { useIsSubscribeMode } from "lib/hooks/useIsSubscribeMode";

const PaymentMethods: React.FC<{}> = ({}) => {
  const { t } = useLocale();
  const {
    state: { paymentMethods },
    actions: { handleNonPaymentOrderPlacement },
  } = usePaymentContext();

  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const isSubscribeMode = useIsSubscribeMode();
  const subscriptionTiles = checkoutMetadata?.uiAttributes?.subscriptionPlanConfig?.paymentTiles;

  const subscriptionTile = (mode?: string) =>
    isSubscribeMode ? subscriptionTiles?.find((tile: PaymentModeUiConfigBE) => tile?.mode === mode) : undefined;

  const [availableMethods, setAvailableMethods] = useState<PaymentMode[]>([]);
  const [unAvailableMethods, setUnAvailableMethods] = useState<PaymentMode[]>([]);

  useEffect(() => {
    if (!paymentMethods) return;

    const { available, unavailable } = paymentMethods.reduce<{
      available: PaymentMode[];
      unavailable: PaymentMode[];
    }>(
      (acc, method) => {
        const methodUiAttributes = checkoutMetadata?.uiAttributes?.paymentTileConfig?.modeDetails?.find(
          (e: PaymentModeUiConfigBE) => e?.mode === method?.name,
        );

        if (SELF_HANDLED_PAYMENT_MODES.includes(method.name)) {
          // ignore the above methods we handle them in their own components
          return acc;
        }

        const tileWeight = subscriptionTile(method.name)?.weight || methodUiAttributes?.weight;

        if (method.isAvailable) {
          acc.available.push({
            ...method,
            weight: tileWeight ? tileWeight : method?.weight,
          });
        } else {
          acc.unavailable.push({
            ...method,
            weight: tileWeight ? tileWeight : method?.weight,
          });
        }
        return acc;
      },
      { available: [], unavailable: [] },
    );


    setAvailableMethods(available);
    setUnAvailableMethods(unavailable);
  }, [paymentMethods, isSubscribeMode, subscriptionTiles]);

  const hasFreeMethod = paymentMethods?.some((e: PaymentMode) => e.name === "FREE");
  const freeMethodEnabled = paymentMethods?.find((e: PaymentMode) => e.name === "FREE")?.isAvailable;
  // Moving these three dorectly into the MethodsContainer component as they have their own unique UI and are not simple cards like the other payment modes. Also they require separate handling on click of place order button
  // const hasGCMode = paymentMethods?.find((e: PaymentMode) => e.name === "GC");
  // const hasShopifyGCMode = paymentMethods?.find((e: PaymentMode) => e.name === "SHOPIFY_GC");
  // const hasCustomPaymentMode = paymentMethods?.find((e: PaymentMode) => e.name === "CUSTOM_PAYMENT");

  // handle upi separately
  const upiIntentMode = paymentMethods?.find((e: PaymentMode) => e.name === "UPI_INTENT");
  const isUpiIntentModeAvailable = upiIntentMode?.isAvailable;


  const shouldShowUPIIntentWithIconsTile =
    Boolean(isUpiIntentModeAvailable) &&
    isS2SVerifiedForUpiIntentTile(getUpiIntentModeConfig(checkoutMetadata, isSubscribeMode), upiIntentMode);

  const modifiedAvailableMethods = useMemo(() => {
    if (!shouldShowUPIIntentWithIconsTile) {
      return availableMethods;
    }
    return availableMethods
      .filter((method) => method.name !== "UPI_INTENT")
      .sort((a, b) => a.weight - b.weight);
  }, [availableMethods, shouldShowUPIIntentWithIconsTile]);

  const modifiedUnAvailableMethods = useMemo(() => {
    if (!shouldShowUPIIntentWithIconsTile) {
      return unAvailableMethods;
    }
    return unAvailableMethods
      .filter((method) => method.name !== "UPI_INTENT")
      .sort((a, b) => a.weight - b.weight);
  }, [unAvailableMethods, shouldShowUPIIntentWithIconsTile]);

  return (
    <section id="payment-methods-section" className="flex flex-col">
      {Boolean(availableMethods.length) && (
        <>
          <div className={`flex w-full flex-row  items-center justify-between px-2 mt-3 mb-3 text-xs`}>
            <h2 className="text-xs font-medium text-coal-light">{t("all_payment_methods")}</h2>
          </div>
          <div
            className={`overflow-clip-margin-2 flex flex-col gap-2 overflow-clip `}
            style={{
              gap: checkoutMetadata?.uiAttributes?.paymentTileConfig?.layoutUI?.paymentTileGap,
            }}>
            {shouldShowUPIIntentWithIconsTile && upiIntentMode && <UPICollect modeData={upiIntentMode} />}
            <MethodsContainer renderMethods={modifiedAvailableMethods} />
          </div>
        </>
      )}

      {Boolean(unAvailableMethods.length) && (
        <>
          <div className={`flex w-full flex-row items-center justify-between gap-1 py-3 text-xs`}>
            <div className="flex w-full flex-col items-start justify-between gap-1">
              <DividerWithLabel className="text-xs uppercase" label={t("unavailable_methods")} />
            </div>
          </div>

          <div className={`overflow-clip-margin-2 flex flex-col space-y-4 overflow-clip`}>
            <MethodsContainer renderMethods={modifiedUnAvailableMethods} />
          </div>
        </>
      )}

      {hasFreeMethod && (
        <>
          <div className="mt-3 w-full">
            <PrimaryButton
              isDisabled={!freeMethodEnabled}
              buttonText={t("place_order")}
              height="h-14"
              onClick={() => handleNonPaymentOrderPlacement("FREE")}
            />
          </div>
        </>
      )}
    </section>
  );
};

export default React.memo(PaymentMethods);
